import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';

// ─── Types (mirror the Pilot Run inspection shape from the web detail page) ──
interface CompositionItem { percentage?: number | string | null; material?: string | null; }
interface ProcessItem { name?: string | null; }
interface ColorCountItem { color?: string | null; count?: string | number | null; unit?: string | null; }
interface PhotoItem { label?: string | null; image_url?: string | null; }
interface CtqImageItem { image_url?: string | null; }

export interface PilotRunPDFData {
  id: string;
  result?: string | null;            // PASS | FAIL | PENDING | COMPLETED
  season?: string | null;
  basis?: string | null;
  ref_number?: string | null;
  buyer_name?: string | null;
  sku_number?: string | null;
  factory_code?: string | null;       // Vendor Product Code
  product_desc?: string | null;       // Vendor Product Description
  total_qty?: string | number | null;
  inspection_date?: string | null;
  created_at?: string | null;
  evaluation?: string | null;
  ctq_remarks?: string | null;
  corrective_actions?: string | null;
  length?: string | number | null; length_unit?: string | null;
  width?: string | number | null; width_unit?: string | null;
  height?: string | number | null; height_unit?: string | null;
  weight?: string | number | null; weight_unit?: string | null;
  compositions?: CompositionItem[];
  processes?: ProcessItem[];
  screens?: ColorCountItem[];
  lifetimes?: ColorCountItem[];
  photos?: PhotoItem[];
  ctqImages?: CtqImageItem[];
  factory_signature?: string | null;
  inspector_signature?: string | null;
  inspector?: { id?: string; name?: string | null } | null;
  inspector_name?: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const formatDate = (raw?: string | null) => {
  if (!raw) return 'N/A';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const dash = (v: any): string => {
  if (v === null || v === undefined) return '—';
  const str = String(v).trim();
  return str.length === 0 ? '—' : str;
};

// ─── Styles (aligned with FinalInspectionReportPDF) ──────────────────────────
const s = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#000000',
    backgroundColor: '#ffffff',
    paddingHorizontal: 36,
    paddingTop: 20,
    paddingBottom: 64,
  },

  // Fixed page header
  topHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  logoText: { fontSize: 16, fontFamily: 'Helvetica-Bold', color: '#2563eb' },
  titleArea: { alignItems: 'flex-end' },
  titleLine1: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#000000' },
  titleLine2: { fontSize: 9, color: '#555555' },
  hr: { borderBottomWidth: 1, borderBottomColor: '#000000', marginBottom: 10 },

  // Result summary row
  resultRow: { flexDirection: 'row', borderWidth: 1, borderColor: '#000000', marginBottom: 12 },
  resultBox: { paddingHorizontal: 12, paddingVertical: 8, borderRightWidth: 1, borderRightColor: '#aaaaaa', justifyContent: 'center', minWidth: 130 },
  resultLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2 },
  resultValue: { fontSize: 20, fontFamily: 'Helvetica-Bold', color: '#000000' },
  statBox: { flex: 1, paddingVertical: 8, paddingHorizontal: 6, borderRightWidth: 0.5, borderRightColor: '#cccccc', alignItems: 'center', justifyContent: 'center' },
  statLabel: { fontSize: 7.5, color: '#666666', textAlign: 'center', marginBottom: 2 },
  statValue: { fontSize: 13, fontFamily: 'Helvetica-Bold', color: '#000000', textAlign: 'center' },

  // Gray section bar
  sectionHeader: { backgroundColor: '#d9d9d9', paddingHorizontal: 8, paddingVertical: 5, marginBottom: 6, marginTop: 4 },
  sectionHeaderText: { fontSize: 10, fontFamily: 'Helvetica-Bold', color: '#000000' },

  // Lighter sub-bar
  subHeader: { backgroundColor: '#f0f0f0', paddingHorizontal: 6, paddingVertical: 3, marginBottom: 4, marginTop: 6 },
  subHeaderText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  // Two-column info grid
  infoGrid: { flexDirection: 'row', flexWrap: 'wrap', marginBottom: 6 },
  infoCell: { width: '33.33%', paddingRight: 8, marginBottom: 8 },
  infoCellHalf: { width: '50%', paddingRight: 8, marginBottom: 8 },
  infoLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2, textTransform: 'uppercase' },
  infoValue: { fontSize: 9, fontFamily: 'Helvetica-Bold', color: '#000000' },

  // Dimensions boxes
  dimRow: { flexDirection: 'row', gap: 8, marginBottom: 6, paddingHorizontal: 4 },
  dimBox: { flex: 1, borderWidth: 0.5, borderColor: '#cccccc', padding: 6, backgroundColor: '#f8f8f8' },
  dimLabel: { fontSize: 7, fontFamily: 'Helvetica-Bold', color: '#666666', marginBottom: 2, textTransform: 'uppercase' },
  dimValue: { fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#000000' },

  // Process chips
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: 4, marginBottom: 8 },
  chip: { borderWidth: 0.5, borderColor: '#999999', backgroundColor: '#f5f5f5', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 3 },
  chipText: { fontSize: 8.5, fontFamily: 'Helvetica-Bold', color: '#333333' },

  // Generic table
  table: { borderWidth: 0.5, borderColor: '#cccccc', marginBottom: 8, marginHorizontal: 4 },
  tableHead: { flexDirection: 'row', backgroundColor: '#f0f0f0', borderBottomWidth: 0.5, borderBottomColor: '#cccccc' },
  tableRow: { flexDirection: 'row', borderTopWidth: 0.5, borderTopColor: '#eeeeee' },
  th: { flex: 1, fontSize: 8, fontFamily: 'Helvetica-Bold', paddingHorizontal: 6, paddingVertical: 3 },
  td: { flex: 1, fontSize: 8.5, paddingHorizontal: 6, paddingVertical: 3 },

  // Photo tiles
  photoGrid: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', paddingHorizontal: 4, marginBottom: 6 },
  photoTile: { width: 120 },
  photoLabel: { fontSize: 7.5, color: '#333333', marginBottom: 3 },
  photoImg: { width: 120, height: 90, objectFit: 'cover' },

  // Evaluation blocks
  evalBlock: { borderWidth: 0.5, borderColor: '#cccccc', padding: 8, marginHorizontal: 4, marginBottom: 8, backgroundColor: '#fafafa' },
  evalLabel: { fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: '#555555', marginBottom: 4, textTransform: 'uppercase' },
  evalText: { fontSize: 9, color: '#222222', lineHeight: 1.4 },

  ctqImgRow: { flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginTop: 6 },
  ctqImg: { width: 110, height: 80, objectFit: 'cover' },

  noDataText: { fontSize: 8, color: '#888888', fontStyle: 'italic', paddingHorizontal: 4, paddingBottom: 6 },

  // Signatures
  sigRow: { flexDirection: 'row', gap: 20, marginTop: 16, paddingHorizontal: 4 },
  sigBox: { flex: 1, borderTopWidth: 1, borderTopColor: '#000000', paddingTop: 6 },
  sigLabel: { fontSize: 8, fontFamily: 'Helvetica-Bold', marginBottom: 4, color: '#444444' },
  sigImg: { width: 110, height: 44, objectFit: 'contain', marginBottom: 4 },
  sigName: { fontSize: 8.5, fontFamily: 'Helvetica-Bold' },
  sigRole: { fontSize: 8, color: '#666666' },
  sigEmpty: { height: 44, marginBottom: 4 },

  // Footer
  pageFooter: {
    position: 'absolute', bottom: 14, left: 36, right: 36,
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
  },
  footerSide: { fontSize: 7.5, color: '#555555', flex: 1 },
  footerCenter: { fontSize: 7.5, color: '#555555', textAlign: 'center', flex: 1 },
  footerRight: { fontSize: 7.5, color: '#555555', textAlign: 'right', flex: 1 },
});

// ─── Shared sub-components ───────────────────────────────────────────────────
const PageTop = () => (
  <>
    <View style={s.topHeader} fixed>
      <Text style={s.logoText}>QA MATE</Text>
      <View style={s.titleArea}>
        <Text style={s.titleLine1}>PILOT RUN INSPECTION REPORT</Text>
        <Text style={s.titleLine2}>QUALITY ASSURANCE DEPARTMENT</Text>
      </View>
    </View>
    <View style={s.hr} fixed />
  </>
);

const PageFooter = () => (
  <View style={s.pageFooter} fixed>
    <Text style={s.footerSide} />
    <Text
      style={s.footerCenter}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
    <Text style={s.footerRight}>QAMATE</Text>
  </View>
);

const SectionBar = ({ title }: { title: string }) => (
  <View style={s.sectionHeader}>
    <Text style={s.sectionHeaderText}>{title}</Text>
  </View>
);

const SubBar = ({ title }: { title: string }) => (
  <View style={s.subHeader}>
    <Text style={s.subHeaderText}>{title}</Text>
  </View>
);

const InfoCell = ({ label, value, half }: { label: string; value: string; half?: boolean }) => (
  <View style={half ? s.infoCellHalf : s.infoCell}>
    <Text style={s.infoLabel}>{label}</Text>
    <Text style={s.infoValue}>{value}</Text>
  </View>
);

const ColorCountTable = ({ title, rows }: { title: string; rows: ColorCountItem[] }) => (
  <>
    <SubBar title={title} />
    <View style={s.table}>
      <View style={s.tableHead}>
        <Text style={s.th}>Color</Text>
        <Text style={s.th}>Number</Text>
        <Text style={s.th}>Unit</Text>
      </View>
      {rows.map((r, i) => (
        <View key={i} style={s.tableRow}>
          <Text style={s.td}>{dash(r.color)}</Text>
          <Text style={s.td}>{dash(r.count)}</Text>
          <Text style={s.td}>{dash(r.unit)}</Text>
        </View>
      ))}
    </View>
  </>
);

// ─── Main PDF Document ───────────────────────────────────────────────────────
const PilotRunReportPDF = ({ inspection }: { inspection: PilotRunPDFData }) => {
  const result = (inspection.result || 'PENDING').toUpperCase();
  const isPassed = result === 'PASS' || result === 'COMPLETED';
  const isFailed = result === 'FAIL';
  const statusLabel = isPassed
    ? (result === 'COMPLETED' ? 'Completed' : 'Approved')
    : isFailed ? 'Rejected' : 'Pending';
  const riskLevel = isPassed ? 'LOW' : isFailed ? 'HIGH' : 'MEDIUM';

  const inspectorName = inspection.inspector?.name || inspection.inspector_name || 'QA Inspector';

  const compositionText = (inspection.compositions || [])
    .map((c) => `${dash(c.percentage)}% ${dash(c.material)}`)
    .filter((t) => t !== '—% —')
    .join(', ');

  const hasDimensions = !!(inspection.length || inspection.width || inspection.height || inspection.weight);
  const processes = (inspection.processes || []).map((p) => p?.name).filter(Boolean) as string[];
  const screens = inspection.screens || [];
  const lifetimes = inspection.lifetimes || [];
  const hasProcessSection = processes.length > 0 || screens.length > 0 || lifetimes.length > 0;

  const photos = (inspection.photos || []).filter((p) => p.image_url);
  const ctqImages = (inspection.ctqImages || []).filter((c) => c.image_url);
  const hasEvaluation = !!(inspection.evaluation || inspection.ctq_remarks || inspection.corrective_actions || ctqImages.length > 0);

  const reportId = `QPR ${inspection.id.slice(0, 6).toUpperCase()}`;

  return (
    <Document title={`Pilot Run Report - ${inspection.sku_number || inspection.id}`}>

      {/* ═══ PAGE 1 — Result + Summary Details (+ Dimensions) ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />

        {/* Result summary row */}
        <View style={s.resultRow}>
          <View style={s.resultBox}>
            <Text style={s.resultLabel}>PILOT RUN RESULT</Text>
            <Text style={s.resultValue}>{result}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Status</Text>
            <Text style={s.statValue}>{statusLabel}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Risk Level</Text>
            <Text style={s.statValue}>{riskLevel}</Text>
          </View>
          <View style={s.statBox}>
            <Text style={s.statLabel}>Season</Text>
            <Text style={s.statValue}>{dash(inspection.season)}</Text>
          </View>
          <View style={[s.statBox, { borderRightWidth: 0 }]}>
            <Text style={s.statLabel}>Report ID</Text>
            <Text style={s.statValue}>{reportId}</Text>
          </View>
        </View>

        {/* Summary Details */}
        <SectionBar title="SUMMARY DETAILS" />
        <View style={s.infoGrid}>
          <InfoCell label="Basis" value={dash(inspection.basis)} />
          <InfoCell label="Ref Number" value={dash(inspection.ref_number)} />
          <InfoCell label="Buyer Name" value={dash(inspection.buyer_name)} />
          <InfoCell label="SKU Number" value={dash(inspection.sku_number)} />
          <InfoCell label="Vendor Product Code" value={dash(inspection.factory_code)} />
          <InfoCell label="Vendor Product Description" value={dash(inspection.product_desc)} />
          <InfoCell label="Total Qty" value={dash(inspection.total_qty)} />
          <InfoCell label="Season" value={dash(inspection.season)} />
          <InfoCell label="Date" value={formatDate(inspection.inspection_date || inspection.created_at)} />
          {compositionText ? (
            <InfoCell label="Composition" value={compositionText} />
          ) : null}
        </View>

        {/* Dimensions */}
        {hasDimensions && (
          <>
            <SectionBar title="DIMENSIONS" />
            <View style={s.dimRow}>
              <View style={s.dimBox}>
                <Text style={s.dimLabel}>Length</Text>
                <Text style={s.dimValue}>{inspection.length ? `${inspection.length} ${dash(inspection.length_unit) === '—' ? '' : inspection.length_unit}` : '—'}</Text>
              </View>
              <View style={s.dimBox}>
                <Text style={s.dimLabel}>Width</Text>
                <Text style={s.dimValue}>{inspection.width ? `${inspection.width} ${dash(inspection.width_unit) === '—' ? '' : inspection.width_unit}` : '—'}</Text>
              </View>
              <View style={s.dimBox}>
                <Text style={s.dimLabel}>Height</Text>
                <Text style={s.dimValue}>{inspection.height ? `${inspection.height} ${dash(inspection.height_unit) === '—' ? '' : inspection.height_unit}` : '—'}</Text>
              </View>
              <View style={s.dimBox}>
                <Text style={s.dimLabel}>Weight</Text>
                <Text style={s.dimValue}>{inspection.weight ? `${inspection.weight} ${dash(inspection.weight_unit) === '—' ? '' : inspection.weight_unit}` : '—'}</Text>
              </View>
            </View>
          </>
        )}

        <PageFooter />
      </Page>

      {/* ═══ PRODUCTION PROCESS ═══ */}
      {hasProcessSection && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="PRODUCTION PROCESS" />

          {processes.length > 0 && (
            <>
              <SubBar title="PROCESSES" />
              <View style={s.chipRow}>
                {processes.map((p, i) => (
                  <View key={i} style={s.chip}>
                    <Text style={s.chipText}>{p}</Text>
                  </View>
                ))}
              </View>
            </>
          )}

          {screens.length > 0 && <ColorCountTable title="NUMBER OF SCREENS" rows={screens} />}
          {lifetimes.length > 0 && <ColorCountTable title="LIFETIME" rows={lifetimes} />}

          <PageFooter />
        </Page>
      )}

      {/* ═══ PILOT RUN IMAGES ═══ */}
      {photos.length > 0 && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="PILOT RUN IMAGES" />
          <View style={s.photoGrid}>
            {photos.map((photo, i) => (
              <View key={i} style={s.photoTile}>
                <Text style={s.photoLabel}>{dash(photo.label) === '—' ? 'Photo' : photo.label}</Text>
                <Image src={photo.image_url!} style={s.photoImg} />
              </View>
            ))}
          </View>
          <PageFooter />
        </Page>
      )}

      {/* ═══ EVALUATION ═══ */}
      {hasEvaluation && (
        <Page size="A4" style={s.page}>
          <PageTop />
          <SectionBar title="EVALUATION" />

          {(inspection.ctq_remarks || ctqImages.length > 0) && (
            <View style={s.evalBlock}>
              <Text style={s.evalLabel}>CTQ Remarks</Text>
              {inspection.ctq_remarks ? <Text style={s.evalText}>{inspection.ctq_remarks}</Text> : null}
              {ctqImages.length > 0 && (
                <View style={s.ctqImgRow}>
                  {ctqImages.map((c, i) => (
                    <Image key={i} src={c.image_url!} style={s.ctqImg} />
                  ))}
                </View>
              )}
            </View>
          )}

          {inspection.evaluation && (
            <View style={s.evalBlock}>
              <Text style={s.evalLabel}>Overall Evaluation</Text>
              <Text style={s.evalText}>{inspection.evaluation}</Text>
            </View>
          )}

          {inspection.corrective_actions && (
            <View style={s.evalBlock}>
              <Text style={s.evalLabel}>Corrective Actions</Text>
              <Text style={s.evalText}>{inspection.corrective_actions}</Text>
            </View>
          )}

          <PageFooter />
        </Page>
      )}

      {/* ═══ SIGNATURES (final page) ═══ */}
      <Page size="A4" style={s.page}>
        <PageTop />
        <SectionBar title="SIGNATURES" />
        <View style={s.sigRow}>
          <View style={s.sigBox}>
            <Text style={s.sigLabel}>Factory Rep Signature</Text>
            {inspection.factory_signature ? (
              <Image src={inspection.factory_signature} style={s.sigImg} />
            ) : (
              <View style={s.sigEmpty} />
            )}
            <Text style={s.sigName}>Factory Representative</Text>
            <Text style={s.sigRole}>Factory Rep</Text>
          </View>

          <View style={s.sigBox}>
            <Text style={s.sigLabel}>QA Signature</Text>
            {inspection.inspector_signature ? (
              <Image src={inspection.inspector_signature} style={s.sigImg} />
            ) : (
              <View style={s.sigEmpty} />
            )}
            <Text style={s.sigName}>{inspectorName}</Text>
            <Text style={s.sigRole}>QA Inspector</Text>
          </View>

          <View style={[s.sigBox, { flex: 0.7 }]}>
            <Text style={s.sigLabel}>Inspection Date</Text>
            <View style={{ height: 44, marginBottom: 4, justifyContent: 'flex-end' }}>
              <Text style={{ fontSize: 11, fontFamily: 'Helvetica-Bold' }}>
                {formatDate(inspection.inspection_date || inspection.created_at)}
              </Text>
            </View>
            <Text style={s.sigRole}>Date of Pilot Run</Text>
          </View>
        </View>

        {/* Brand footer on last page */}
        <View style={{
          position: 'absolute', bottom: 14, left: 36, right: 36,
          flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
          borderTopWidth: 0.5, borderTopColor: '#cccccc', paddingTop: 4,
        }}>
          <Text
            style={{ fontSize: 7.5, color: '#555555', flex: 1, textAlign: 'center' }}
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
          />
          <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#2563eb' }}>QAMATE</Text>
            <Text style={{ fontSize: 7.5, color: '#555555' }}>info@qamate.in | www.qamate.in</Text>
          </View>
        </View>
      </Page>

    </Document>
  );
};

export default PilotRunReportPDF;
